// Story 16.5 — organizaciones, miembros e invitaciones. Las mutaciones
// invalidan lo que cambia, incluido `/auth/me` (lista las organizaciones del
// usuario y sus capabilities).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { Capability, OrganizationRole } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-client';

export interface OrganizationSummary {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  memberCount: number;
  botCount: number;
  /** Rol del usuario en la organización; `null` si la ve como staff. */
  role: OrganizationRole | null;
  capabilities: Capability[];
}

export interface OrganizationMember {
  userId: number;
  email: string;
  name: string | null;
  role: OrganizationRole;
  createdAt: string;
}

export interface OrganizationInvitation {
  id: number;
  email: string;
  role: OrganizationRole;
  expiresAt: string;
  createdAt: string;
  expired: boolean;
}

export interface CreatedInvitation {
  invitation: OrganizationInvitation;
  token: string;
  /** `/invite#<token>`: el token va en el fragmento, que no sale del navegador. */
  path: string;
}

export function useOrganizations() {
  return useQuery({
    queryKey: queryKeys.organizations(),
    queryFn: () => api.get<OrganizationSummary[]>('/api/v1/organizations'),
  });
}

export function useOrganization(id: number) {
  return useQuery({
    queryKey: queryKeys.organization(id),
    queryFn: () => api.get<OrganizationSummary>(`/api/v1/organizations/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function useOrganizationMembers(id: number, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.organizationMembers(id),
    queryFn: () => api.get<OrganizationMember[]>(`/api/v1/organizations/${id}/members`),
    enabled,
  });
}

export function useOrganizationInvitations(id: number, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.organizationInvitations(id),
    queryFn: () => api.get<OrganizationInvitation[]>(`/api/v1/organizations/${id}/invitations`),
    enabled,
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<OrganizationSummary>('/api/v1/organizations', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.organizations() }),
  });
}

/** Mutaciones sobre una organización; cada una refresca lo que toca. */
export function useOrganizationMutations(id: number) {
  const qc = useQueryClient();
  const base = `/api/v1/organizations/${id}`;
  const refresh = (...keys: ReadonlyArray<readonly unknown[]>) => {
    for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
  };
  return {
    rename: useMutation({
      mutationFn: (name: string) => api.put(base, { name }),
      onSuccess: () =>
        refresh(queryKeys.organization(id), queryKeys.organizations(), queryKeys.me(), ['bots']),
    }),
    changeRole: useMutation({
      mutationFn: (v: { userId: number; role: OrganizationRole }) =>
        api.put(`${base}/members/${v.userId}`, { role: v.role }),
      // Puede ser el propio rol: `me` y los bots cambian de capabilities.
      onSuccess: () =>
        refresh(queryKeys.organizationMembers(id), queryKeys.organization(id), queryKeys.me(), [
          'bots',
        ]),
    }),
    removeMember: useMutation({
      mutationFn: (userId: number) => api.delete(`${base}/members/${userId}`),
      onSuccess: () =>
        refresh(queryKeys.organizationMembers(id), queryKeys.organization(id), queryKeys.me()),
    }),
    invite: useMutation({
      mutationFn: (v: { email: string; role: OrganizationRole }) =>
        api.post<CreatedInvitation>(`${base}/invitations`, v),
      onSuccess: () => refresh(queryKeys.organizationInvitations(id)),
    }),
    revokeInvitation: useMutation({
      mutationFn: (invitationId: number) => api.delete(`${base}/invitations/${invitationId}`),
      onSuccess: () => refresh(queryKeys.organizationInvitations(id)),
    }),
  };
}
